<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useAuthStore, useNotificationStore } from '@/stores'
import { useGoogleTranslate } from '@/composables/useGoogleTranslate'
import FirebaseChatWidget from '@/components/chat/FirebaseChatWidget.vue'
import api from '@/services/api'
import logoImg from '@/assets/logo.png'

const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const router = useRouter()

// Google Translate
const { currentLanguage, translatorReady, initialize: initTranslator, changeLanguage } = useGoogleTranslate({
  pageLanguage: 'en',
  includedLanguages: 'en,bn'
})

// Sidebar state
const sidebarOpen = ref(false)
const activeSubmenu = ref<string | null>(null)

// Notification popup
const notificationPopupOpen = ref(false)

// Profile dropdown
const profileDropdownOpen = ref(false)

// Search
const searchQuery = ref('')

// Countries data for menus
const packageCountries = ref<Array<{id: number, name: string}>>([])
const visaCountries = ref<Array<{id: number, name: string}>>([])
const transportCountries = ref<Array<{id: number, name: string}>>([])

// Balance data
const availableBalance = ref(0)

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
  if (sidebarOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

function closeSidebar() {
  sidebarOpen.value = false
  document.body.style.overflow = ''
}

function toggleNotificationPopup() {
  notificationPopupOpen.value = !notificationPopupOpen.value
  if (notificationPopupOpen.value && notificationStore.notifications.length === 0) {
    notificationStore.fetchAll()
  }
}

function closeNotificationPopup() {
  notificationPopupOpen.value = false
}

function toggleProfileDropdown() {
  profileDropdownOpen.value = !profileDropdownOpen.value
}

function closeProfileDropdown() {
  profileDropdownOpen.value = false
}

function toggleSubmenu(menu: string) {
  activeSubmenu.value = activeSubmenu.value === menu ? null : menu
}

function handleSearch() {
  if (searchQuery.value.trim()) {
    router.push(`/search?q=${encodeURIComponent(searchQuery.value)}`)
    searchQuery.value = ''
  }
}

async function handleLogout() {
  closeProfileDropdown()
  await authStore.logout()
}

// Close sidebar when clicking overlay
function handleOverlayClick() {
  closeSidebar()
}

// Load countries for dropdown menus
async function loadCountries() {
  try {
    const [packagesRes, visaRes] = await Promise.all([
      api.get('/package-countries'),
      api.get('/visa-countries')
    ])
    packageCountries.value = packagesRes.data.data || packagesRes.data || []
    visaCountries.value = visaRes.data.data || visaRes.data || []
    // Transport countries same as package countries
    transportCountries.value = packagesRes.data.data || packagesRes.data || []
  } catch (error) {
    console.warn('Failed to load countries:', error)
  }
}

// Load balance
async function loadBalance() {
  try {
    const res = await api.get('/company/balance')
    // console.log('Balance response:', res.data.data)
    availableBalance.value = res.data.data.balance || 0
  } catch (error) {
    console.warn('Failed to load balance:', error)
  }
}

// Load notification count
async function loadNotificationCount() {
  try {
    await notificationStore.fetchLatest()
  } catch (error) {
    console.warn('Failed to load notifications:', error)
  }
}

async function markNotificationAsRead(notificationId: number) {
  await notificationStore.markAsRead(notificationId)
}

// Close sidebar on Escape key
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    if (sidebarOpen.value) {
      closeSidebar()
    }
    if (notificationPopupOpen.value) {
      closeNotificationPopup()
    }
    if (profileDropdownOpen.value) {
      closeProfileDropdown()
    }
  }
}

// Close popup when clicking outside
function handleDocumentClick(e: MouseEvent) {
  const popup = document.getElementById('notification-popup')
  const bell = document.getElementById('notification-bell')
  if (popup && bell && !popup.contains(e.target as Node) && !bell.contains(e.target as Node)) {
    closeNotificationPopup()
  }
}

onMounted(() => {
  loadCountries()
  loadBalance()
  loadNotificationCount()
  initTranslator()
  
  document.addEventListener('keydown', handleKeydown)
  document.addEventListener('click', handleDocumentClick)
  
  // Close profile dropdown when clicking outside
  document.addEventListener('click', (e: MouseEvent) => {
    const profile = document.querySelector('.user-profile')
    if (profile && !profile.contains(e.target as Node)) {
      closeProfileDropdown()
    }
  })
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<template>
  <header class="main-header">
    <div class="container">
      <!-- Sidebar toggle button (Menu button) - Fixed position -->
      <button 
        class="blink-menu-btn d-none d-md-flex" 
        id="sidebarToggle"
        @click="toggleSidebar"
      >
        <span>Menu👈</span>
      </button>
      
      <div class="header-content">
        <!-- Logo -->
        <RouterLink to="/" class="logo-link">
          <img :src="logoImg" alt="B2B Tour Deal" />
        </RouterLink>
        
        
        
        <!-- Header Right Side -->
        <div class="header-right">
          <!-- Search Box -->
        <div class="search-box">
          <form @submit.prevent="handleSearch" class="search-form">
            <input 
              v-model="searchQuery" 
              type="text" 
              class="search-input" 
              placeholder="Search..." 
            />
            <button type="submit" class="search-btn">Search</button>
          </form>
        </div>

          <!-- Language Selector -->
          <div class="language-selector">
            <select
              class="lang-select"
              :value="currentLanguage"
              @change="changeLanguage(($event.target as HTMLSelectElement).value)"
              :disabled="!translatorReady"
              aria-label="Select language"
              title="Select language"
            >
              <option value="en">EN</option>
              <option value="bn">BN</option>
            </select>
          </div>

          <!-- Google Translate (hidden but functional) -->
          <div id="google_translate_element"></div>

          <!-- Finance Summary -->
          <RouterLink 
            to="/finance" 
            class="finance-summary-btn"
            :class="availableBalance >= 0 ? 'balance-positive' : 'balance-negative'"
          >
            <i class="fa fa-wallet"></i>
            <span class="balance-amount d-none d-md-flex">
              {{ availableBalance >= 0 ? 'Balance:' : 'Due:' }}
            </span>
            <span class="balance-amount">
              {{ Math.abs(availableBalance).toLocaleString() }} ৳
            </span>
          </RouterLink>
          
          <!-- Notification Bell -->
          <button 
            id="notification-bell" 
            class="notification-bell" 
            @click="toggleNotificationPopup"
          >
            <i class="fa fa-bell"></i>
            <span 
              v-if="notificationStore.unreadCount > 0" 
              id="notification-count" 
              class="notification-count notify-animate"
            >
              {{ notificationStore.unreadCount }}
            </span>
          </button>
          
          <!-- User Profile Dropdown -->
          <div class="user-profile d-none d-md-block">
            <div class="profile-trigger" @click.stop="toggleProfileDropdown">
              <h6 class="m-0">
                <span class="user-name">
                  <i class="fa fa-user-circle"></i>
                  {{ authStore.user?.company?.code || authStore.user?.name || 'User' }}
                </span>
              </h6>
            </div>
            
            <div class="dropdown-menu" :class="{ active: profileDropdownOpen }"  @click.stop>
              <div class="dropdown-header">
                <h6>{{ authStore.user?.company?.name || 'Company Name' }}</h6>
                <p>{{ authStore.user?.name }}</p>
                <small>{{ authStore.user?.email }}</small>
              </div>
              
              <!-- <div class="dropdown-header">
                <RouterLink to="/company/profile" @click="closeProfileDropdown">
                  Company Profile
                </RouterLink>
              </div>
              
              <div v-if="authStore.hasPermission('edit_company_profile')" class="dropdown-header">
                <RouterLink to="/company/edit" @click="closeProfileDropdown">
                  Update Company Profile
                </RouterLink>
              </div> -->
              
              <div class="dropdown-header">
                <RouterLink to="/change-password" @click="closeProfileDropdown">
                  Change Password
                </RouterLink>
              </div>
              
              <!-- <div class="dropdown-header">
                <RouterLink to="/package-requests" @click="closeProfileDropdown">
                  My Package Requests
                </RouterLink>
              </div> -->
              
              <div class="dropdown-content">
                <hr />
                <a href="#" @click.prevent="handleLogout" class="logout-btn">
                  Logout
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Mobile Menu Button -->
      <button type="button" class="navigation-slide-btn d-md-none" @click="toggleSidebar"></button>
    </div>
    
    <!-- Notification Popup -->
    <div 
      v-if="notificationPopupOpen" 
      id="notification-popup" 
      class="notification-popup"
    >
      <div class="notification-popup-header">
        <span><i class="fa fa-bell me-2"></i>Notifications</span>
        <button @click="closeNotificationPopup" class="close-btn">&times;</button>
      </div>
      <div class="notification-popup-list">
        <div v-if="notificationStore.loading" class="notification-item">
          Loading...
        </div>
        <div v-else-if="notificationStore.notifications.length === 0" class="notification-item">
          No notifications
        </div>
        <div 
          v-else 
          v-for="notification in notificationStore.notifications.slice(0, 5)" 
          :key="notification.id"
          class="notification-item"
          :class="{ unread: notification.pivot && !notification.pivot.read_at }"
        >
          <div class="notification-title">{{ notification.title }}</div>
          <div v-if="notification.body" v-html="notification.body" class="notification-body"></div>
          <div class="notification-footer">
            <small>{{ new Date(notification.created_at).toLocaleDateString() }}</small>
            <button 
              v-if="notification.pivot && !notification.pivot.read_at"
              @click="markNotificationAsRead(notification.id)"
              class="btn-mark-read"
            >
              Mark as read
            </button>
          </div>
        </div>
      </div>
      <div class="notification-popup-footer">
        <RouterLink to="/notifications" @click="closeNotificationPopup">View All</RouterLink>
      </div>
    </div>
    
    <!-- Sidebar Navigation -->
    <div 
      id="sidebarNav" 
      class="sidebar-nav"
      :class="{ 'sidebar-open': sidebarOpen }"
    >
      <!-- Sidebar Header with Logo and Close Button -->
      <div class="sidebar-header">
        <RouterLink to="/" class="sidebar-logo" @click="closeSidebar">
          <img :src="logoImg" alt="B2B Tour Deal" />
        </RouterLink>
        <button class="sidebar-close" @click="closeSidebar">&times;</button>
      </div>
     
      <nav class="sidebar-menu">
        <ul class="sidebar-list">
          <!-- Dashboard -->
          <li>
            <RouterLink to="/home" class="sidebar-link" @click="closeSidebar">
              <i class="fa fa-tachometer-alt"></i> Dashboard
            </RouterLink>
          </li>
          
          <!-- Request Custom Package -->
          <li v-if="authStore.hasPermission('create_package_requests')">
            <RouterLink to="/package-requests" class="sidebar-link sidebar-link-primary" @click="closeSidebar">
              <i class="fa fa-envelope-open"></i> Request Custom Package
            </RouterLink>
          </li>
          
          <!-- Packages with submenu -->
          <li class="sidebar-has-submenu" :class="{ 'active': activeSubmenu === 'packages' }">
            <a href="#" class="sidebar-link sidebar-submenu-toggle sidebar-link-orange" @click.prevent="toggleSubmenu('packages')">
              <i class="fa fa-suitcase"></i> Packages
              <i class="fa fa-chevron-down submenu-arrow"></i>
            </a>
            <ul class="sidebar-submenu" v-show="activeSubmenu === 'packages'">
              <li>
                <RouterLink to="/packages" @click="closeSidebar">All Packages</RouterLink>
              </li>
              <li v-for="country in packageCountries" :key="country.id">
                <RouterLink :to="`/packages?country_id=${country.id}`" @click="closeSidebar">
                  {{ country.name }}
                </RouterLink>
              </li>
            </ul>
          </li>
          
          <!-- Transport with submenu -->
          <li class="sidebar-has-submenu" :class="{ 'active': activeSubmenu === 'transport' }">
            <a href="#" class="sidebar-link sidebar-submenu-toggle sidebar-link-red" @click.prevent="toggleSubmenu('transport')">
              <i class="fa fa-bus"></i> Transport
              <i class="fa fa-chevron-down submenu-arrow"></i>
            </a>
            <ul class="sidebar-submenu" v-show="activeSubmenu === 'transport'">
              <li>
                <RouterLink to="/transports" @click="closeSidebar">All Transports</RouterLink>
              </li>
              <li v-for="country in transportCountries" :key="country.id">
                <RouterLink :to="`/transports?country_id=${country.id}`" @click="closeSidebar">
                  {{ country.name }}
                </RouterLink>
              </li>
            </ul>
          </li>
          
          <!-- Visa with submenu -->
          <li class="sidebar-has-submenu" :class="{ 'active': activeSubmenu === 'visa' }">
            <a href="#" class="sidebar-link sidebar-submenu-toggle sidebar-link-green" @click.prevent="toggleSubmenu('visa')">
              <i class="fa fa-id-card"></i> Visa
              <i class="fa fa-chevron-down submenu-arrow"></i>
            </a>
            <ul class="sidebar-submenu" v-show="activeSubmenu === 'visa'">
              <li>
                <RouterLink to="/visa" @click="closeSidebar">All Visa</RouterLink>
              </li>
              <li v-for="country in visaCountries" :key="country.id">
                <RouterLink :to="`/visa?country_id=${country.id}`" @click="closeSidebar">
                  {{ country.name }}
                </RouterLink>
              </li>
            </ul>
          </li>
          
          <!-- Booking Vouchers -->
          <li>
            <RouterLink to="/booking-vouchers" class="sidebar-link" @click="closeSidebar">
              <i class="fa fa-ticket-alt"></i> Booking Vouchers
            </RouterLink>
          </li>
          
          <!-- Invoices -->
          <li>
            <RouterLink to="/invoices" class="sidebar-link" @click="closeSidebar">
              <i class="fa fa-file-invoice"></i> Invoices
            </RouterLink>
          </li>
          
          <!-- Finance with submenu -->
          <li class="sidebar-has-submenu" :class="{ 'active': activeSubmenu === 'finance' }">
            <a href="#" class="sidebar-link sidebar-submenu-toggle sidebar-link-green" @click.prevent="toggleSubmenu('finance')">
              <i class="fa fa-chart-line"></i> Finance
              <i class="fa fa-chevron-down submenu-arrow"></i>
            </a>
            <ul class="sidebar-submenu" v-show="activeSubmenu === 'finance'">
              <li>
                <RouterLink to="/finance" @click="closeSidebar">Dashboard</RouterLink>
              </li>
              <li v-if="authStore.hasPermission('submit_balance_requests')">
                <RouterLink to="/finance/balance-requests/create" @click="closeSidebar">Top Up Request</RouterLink>
              </li>
              <li>
                <RouterLink to="/finance/balance-requests" @click="closeSidebar">Payment History</RouterLink>
              </li>
              <li>
                <RouterLink to="/finance/ledger" @click="closeSidebar">Ledger</RouterLink>
              </li>
              <li>
                <RouterLink to="/finance/refunds" @click="closeSidebar">Refund History</RouterLink>
              </li>
            </ul>
          </li>
          
          <!-- Visa Status (Passports) -->
          <li>
            <RouterLink to="/passports" class="sidebar-link" @click="closeSidebar">
              <i class="fa fa-passport"></i> Visa Status
            </RouterLink>
          </li>
          
          
          <!-- Divider -->
          <li class="sidebar-divider"></li>
          
          <!-- Administration with submenu -->
          <li class="sidebar-has-submenu" :class="{ 'active': activeSubmenu === 'admin' }">
            <a href="#" class="sidebar-link sidebar-submenu-toggle sidebar-link-purple" @click.prevent="toggleSubmenu('admin')">
              <i class="fa fa-cog"></i> Admin Settings
              <i class="fa fa-chevron-down submenu-arrow"></i>
            </a>
            <ul class="sidebar-submenu" v-show="activeSubmenu === 'admin'">
              <li>
                <RouterLink to="/company/profile" @click="closeSidebar">
                  <i class="fa fa-building me-1"></i> Company Profile
                </RouterLink>
              </li>
              <li v-if="authStore.hasPermission('view_users')">
                <RouterLink to="/users" @click="closeSidebar">
                  <i class="fa fa-users me-1"></i> User Management
                </RouterLink>
              </li>
              <li v-if="authStore.hasPermission('view_users')">
                <RouterLink to="/users/create" @click="closeSidebar">
                  <i class="fa fa-user-plus me-1"></i> Add New User
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/change-password" @click="closeSidebar">
                  <i class="fa fa-key me-1"></i> Change Password
                </RouterLink>
              </li>
              <!-- notification-settings -->
              <li>
                <RouterLink to="/notification-settings" @click="closeSidebar">
                  <i class="fa fa-bell"></i> Notification Settings
                </RouterLink>
              </li>
            </ul>
          </li>
          
          <!-- Logout -->
          <li>
            <a href="#" class="sidebar-link sidebar-link-danger" @click.prevent="handleLogout">
              <i class="fa fa-sign-out-alt"></i> Logout
            </a>
          </li>
        </ul>
      </nav>
    </div>
    
    <!-- Overlay for sidebar -->
    <div 
      class="nav-overlay" 
      :class="{ 'active': sidebarOpen }"
      @click="handleOverlayClick"
    ></div>
  </header>

  <FirebaseChatWidget v-if="authStore.isAuthenticated" />
</template>

<style scoped>
@import url("./AppHeader.css");
</style>

<style>
iframe.goog-te-banner-frame,
iframe.goog-te-banner-frame.skiptranslate,
body > .skiptranslate,
#goog-gt-tt,
.goog-tooltip,
.goog-text-highlight {
  display: none !important;
}

html,
body {
  top: 0 !important;
}
</style>

